import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import axios from "axios";
import { format } from "date-fns";
import { Field, Form, Formik } from "formik";
import { initialValues, validationSchema } from "./constants";
import "./CandidateEducationDetailsSection.scss";

import { CandidateEntityConstant } from "@workspace/candidate/src/constants/candidateConstant";
import {
  postCandidate,
  putCandidate,
  fetchCandidate,
} from "@workspace/candidate/src/store/candidate/action";
import { CANDIDATE_EDUCATION_DETAILS_URL } from "@workspace/common/src/config";
import { BASE_CANDIDATE_EDUCATION_DETAILS } from "@workspace/portfolio/src/helpers/url_helper";
import {
  fetchCandidateEducation,
  deleteCandidateEducation,
} from "@workspace/portfolio/src/store/portfolio/action";

export const CandidateEducationDetailsSection = ({ id, publicView }) => {
  const dispatch = useDispatch();

  const candidateEducation = useSelector(
    (state) => state?.PortfolioReducer?.candidateEducation
  );
  const candidate = useSelector((state) => state?.CandidateReducer?.candidate);

  const [isExpanded, setIsExpanded] = useState(true);
  const [addEducationExpanded, setAddEducationExpanded] = useState(false);
  const [candidateEducationData, setCandidateEducationData] = useState([]);
  const [publicEducationData, setPublicEducationData] = useState(null);

  // State to track which education entry is in edit mode
  const [editModeId, setEditModeId] = useState(null);

  // Retrieve Candidate Education Details
  useEffect(() => {
    if (id) {
      if (publicView) {
        axios
          .get(
            `${CANDIDATE_EDUCATION_DETAILS_URL}${BASE_CANDIDATE_EDUCATION_DETAILS}/public/entity/${CandidateEntityConstant.CANDIDATE_EDUCATION_DETAILS}/${id}`
          )
          .then((response) => {
            setPublicEducationData(response.data);
          });
      } else {
        dispatch(
          fetchCandidateEducation({
            entity: CandidateEntityConstant.CANDIDATE_EDUCATION_DETAILS,
            id: id,
          })
        );
      }
    }
  }, [id, publicView]);

  // Parse Submission Data
  const parseEducationDetails = (educationDetails) => {
    const parsedData = educationDetails?.map((item) => {
      const submissionData = JSON.parse(item.submissionData);
      for (const key in submissionData) {
        if (submissionData[key] === "") {
          submissionData[key] = null;
        }
      }
      return {
        ...item,
        submissionData,
      };
    });
    return parsedData;
  };

  // Parse Candidate Education Details
  useEffect(() => {
    if (publicEducationData != null) {
      const parsedData = parseEducationDetails(publicEducationData);
      setCandidateEducationData(parsedData);
    } else if (candidateEducation != null) {
      const parsedData = parseEducationDetails(candidateEducation);
      setCandidateEducationData(parsedData);
    }
  }, [candidateEducation, publicEducationData]);

  // Check for Candidate Changes
  useEffect(() => {
    const isFormIdMatch = candidateEducation.some(
      (item) => item.formId === candidate?.formId
    );

    if (isFormIdMatch) {
      let updateCandidateEducationDetails;

      // Find if the formSubmissionId matches any in candidateWorkExperience
      const isFormSubmissionIdMatch = candidateEducation.some(
        (item) => item.formSubmissionId === candidate?.formSubmissionId
      );

      if (isFormSubmissionIdMatch) {
        updateCandidateEducationDetails = candidateEducation.map((item) =>
          item.id === candidate.id ? candidate : item
        );
      } else {
        updateCandidateEducationDetails = [...candidateEducation, candidate];
      }

      const parsedEducationDetails = parseEducationDetails(
        updateCandidateEducationDetails
      );
      setCandidateEducationData(parsedEducationDetails);
      dispatch(fetchCandidate(id));
    }
  }, [candidate]);

  // Handle Submit
  const handleSubmit = async (values, educationId) => {
    const formId = candidateEducationData[0]?.formId;

    const newData = {
      ...values,
      entityId: id,
      entityType: CandidateEntityConstant.CANDIDATE_EDUCATION_DETAILS,
      formData: JSON.stringify(values),
      formId: parseInt(formId),
    };

    await dispatch(
      putCandidate({
        entity: CandidateEntityConstant.CANDIDATE_EDUCATION_DETAILS,
        id: educationId.educationId,
        newData: newData,
      })
    ).then(() => {
      toast.success("Education details updated successfully.");
      dispatch(fetchCandidate(id));
    });

    // Exit edit mode after saving
    setEditModeId(null);
  };

  // Handle Delete
  const handleDelete = (id) => {
    dispatch(deleteCandidateEducation(id));
  };

  // Handle New Education
  const handleNewEducation = (values) => {
    const formId = candidateEducationData[0]?.formId;
    const newData = {
      ...values,
      entityId: id,
      entityType: CandidateEntityConstant.CANDIDATE_EDUCATION_DETAILS,
      formData: JSON.stringify(values),
      formId: parseInt(formId),
    };

    dispatch(
      postCandidate({
        entity: CandidateEntityConstant.CANDIDATE_EDUCATION_DETAILS,
        newData: newData,
      })
    );

    setAddEducationExpanded(false);
  };

  // Toggle edit mode for a specific education entry
  const toggleEditMode = (id) => {
    setEditModeId((prevId) => (prevId === id ? null : id));
  };

  return (
    <>
      <div className="col-12 d-flex flex-row gap-3 align-items-center fw-semibold mb-2">
        <span
          className={`mdi ${
            isExpanded ? "mdi-chevron-up" : "mdi-chevron-down"
          } cursor-pointer`}
          onClick={() => setIsExpanded(!isExpanded)}
        ></span>
        <span>My Education</span>
      </div>
      <div
        className={`transition-container mb-3 ${
          isExpanded ? "expanded" : "collapsed"
        }`}
      >
        {candidateEducationData && candidateEducationData?.length > 0 ? (
          candidateEducationData?.map((educationData, index) => {
            const education = educationData?.submissionData;
            const isEditMode = editModeId === educationData.id;

            return (
              <React.Fragment key={index}>
                <div className="row w-100 d-flex align-items-start mb-3">
                  <div className="col-6">
                    <div
                      className="h4"
                      style={{ color: "#3256ED", fontWeight: "lighter" }}
                    >
                      {education?.qualification} in {education?.fieldOfStudy}
                    </div>
                    <div>{education?.institution}</div>
                  </div>
                  <div className="col-5">
                    <div className="fw-semibold">
                      {education?.startDate
                        ? format(new Date(education?.startDate), "MMMM yyyy")
                        : null}{" "}
                      -{" "}
                      {education?.graudationDate
                        ? format(
                            new Date(education?.graudationDate),
                            "MMMM yyyy"
                          )
                        : null}
                    </div>
                  </div>
                  <div className="col-1 d-flex justify-content-end">
                    <div className="d-flex align-items-center gap-3">
                      <div
                        style={{
                          width: "22px",
                          height: "22px",
                          backgroundColor: "#E7E9EB",
                          color: "#000",
                          visibility: publicView ? "hidden" : "visible",
                        }}
                        className="d-flex align-items-center justify-content-center rounded-circle cursor-pointer"
                        onClick={() => toggleEditMode(educationData.id)}
                      >
                        <span className="mdi mdi-pencil"></span>
                      </div>
                      <div
                        style={{
                          width: "22px",
                          height: "22px",
                          backgroundColor: "#E7E9EB",
                          color: "#000",
                          visibility: publicView ? "hidden" : "visible",
                        }}
                        className="d-flex align-items-center justify-content-center rounded-circle cursor-pointer"
                        onClick={() => handleDelete(educationData?.id)}
                      >
                        <span className="mdi mdi-delete-outline"></span>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Edit Form */}
                <div
                  className={`transition-container ${
                    isEditMode ? "expanded" : "collapsed"
                  }`}
                >
                  <Formik initialValues={education} enableReinitialize={true}>
                    {({ values, resetForm }) => (
                      <Form
                        className={`transition-container row my-3 ${
                          isEditMode ? "expanded" : "collapsed"
                        }`}
                      >
                        <div className="col-4 mb-3">
                          <span className="text-muted">Qualification</span>
                          <Field
                            name="qualification"
                            type="text"
                            placeholder="Enter Qualification"
                            className="form-control"
                            style={{ border: "1px solid #E7E9EB" }}
                          />
                        </div>
                        <div className="col-4 mb-3">
                          <span className="text-muted">Institution</span>
                          <Field
                            name="institution"
                            type="text"
                            placeholder="Enter Institution"
                            className="form-control"
                            style={{ border: "1px solid #E7E9EB" }}
                          />
                        </div>
                        <div className="col-4 mb-3">
                          <span className="text-muted">Field of Study</span>
                          <Field
                            name="fieldOfStudy"
                            type="text"
                            placeholder="Enter Field of Study"
                            className="form-control"
                            style={{ border: "1px solid #E7E9EB" }}
                          />
                        </div>
                        <div className="col-4 mb-3">
                          <span className="text-muted">Start Date</span>
                          <Field
                            name="startDate"
                            type="date"
                            className="form-control"
                            style={{ border: "1px solid #E7E9EB" }}
                          />
                        </div>
                        <div className="col-4 mb-3">
                          <span className="text-muted">Graduation Date</span>
                          <Field
                            name="graudationDate"
                            type="date"
                            className="form-control"
                            style={{ border: "1px solid #E7E9EB" }}
                          />
                        </div>
                        <div className="d-flex flex-row gap-3 align-items-center mb-3">
                          <div
                            onClick={() => {
                              handleSubmit(values, {
                                educationId: educationData?.id,
                              });
                              resetForm();
                            }}
                            className="cursor-pointer"
                            style={{ color: "#10B967" }}
                          >
                            <span className="mdi mdi-check me-2"></span>
                            <span>Save</span>
                          </div>
                          <div
                            onClick={() => {
                              toggleEditMode(null);
                              resetForm();
                            }}
                            className="cursor-pointer"
                            style={{ color: "#C71010" }}
                          >
                            <span className="mdi mdi-close me-2"></span>
                            <span>Cancel</span>
                          </div>
                        </div>
                      </Form>
                    )}
                  </Formik>
                </div>
              </React.Fragment>
            );
          })
        ) : (
          <span className="text-muted">No education details found.</span>
        )}
        {/* Add New Education Data */}
        <div
          className="new-education-btn mb-3"
          onClick={() => setAddEducationExpanded(!addEducationExpanded)}
          style={{ visibility: publicView ? "hidden" : "visible" }}
        >
          <span className="mdi mdi-plus me-2"></span>
          <span>Add New Education</span>
        </div>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
        >
          {({ values, resetForm }) => {
            return (
              <Form
                className={`transition-container ${
                  addEducationExpanded ? "expanded" : "collapsed"
                }`}
              >
                <div className="row">
                  <div className="col-4 mb-3">
                    <span className="text-muted">Qualification</span>
                    <Field
                      name="qualification"
                      type="text"
                      placeholder="Enter Qualification"
                      className="form-control"
                      style={{ border: "1px solid #E7E9EB" }}
                    />
                  </div>
                  <div className="col-4 mb-3">
                    <span className="text-muted">Institution</span>
                    <Field
                      name="institution"
                      type="text"
                      placeholder="Enter Institution"
                      className="form-control"
                      style={{ border: "1px solid #E7E9EB" }}
                    />
                  </div>
                  <div className="col-4 mb-3">
                    <span className="text-muted">Field of Study</span>
                    <Field
                      name="fieldOfStudy"
                      type="text"
                      placeholder="Enter Field of Study"
                      className="form-control"
                      style={{ border: "1px solid #E7E9EB" }}
                    />
                  </div>
                  <div className="col-4 mb-3">
                    <span className="text-muted">Start Date</span>
                    <Field
                      name="startDate"
                      type="date"
                      className="form-control"
                      style={{ border: "1px solid #E7E9EB" }}
                    />
                  </div>
                  <div className="col-4 mb-3">
                    <span className="text-muted">Graduation Date</span>
                    <Field
                      name="graudationDate"
                      type="date"
                      className="form-control"
                      style={{ border: "1px solid #E7E9EB" }}
                    />
                  </div>
                  <div className="d-flex flex-row gap-3 align-items-center">
                    <div
                      onClick={() => {
                        handleNewEducation(values);
                        resetForm();
                      }}
                      className="cursor-pointer"
                      style={{ color: "#10B967" }}
                    >
                      <span className="mdi mdi-check me-2"></span>
                      <span>Save</span>
                    </div>
                    <div
                      onClick={resetForm}
                      className="cursor-pointer"
                      style={{ color: "#C71010" }}
                    >
                      <span className="mdi mdi-close me-2"></span>
                      <span>Cancel</span>
                    </div>
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
      <hr />
    </>
  );
};
